'use client';

import Image from 'next/image'
import { useEffect, useState } from 'react';
import ArrowHandDrawn from '../assets/ArrowHandDrawn';
import Bubble from '../assets/Bubble';

type HeroAboutProps = {
  id: string;
};

export default function HeroAbout({ id }: HeroAboutProps) {
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [hasFadedOut, setHasFadedOut] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (hasFadedOut) return; // Once faded out, stop listening
      
      const scrollY = window.scrollY;
      // Fade out over first 200px of scroll
      const opacity = Math.max(0, 1 - scrollY / 200);
      setScrollOpacity(opacity);
      
      // Lock it at 0 once fully faded
      if (opacity === 0) {
        setHasFadedOut(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasFadedOut]);

  return (
    <section className="about-hero relative min-h-[100vh] lg:min-h-[100vh] flex flex-col items-center pt-16 pb-12 lg:pt-24 lg:pb-2" id={id}>
      
      {/* Desktop version */}
      <div className="global-padding max-w-120rem animated-text-desktop animated-text-desktop--about hidden mt-10 lg:flex w-full flex-1 items-center">
        <h1
          className="heading-1-sm tracking-tight text-gray-800  mx-auto about-text-container"
          aria-label="We bring together ideas, design and technology to create extraordinary customer experiences."
        >
          <span>W</span>
          <span>e</span>
          <span className="inline-block w-7"></span>
          <span>b</span>
          <span>r</span>
          <span>i</span>
          <span>n</span>
          <span>g</span>
          <span className="inline-block w-7"></span>
          <span>t</span>
          <span className="italic">o</span>
          <span>g</span>
          <span>e</span>
          <span>t</span>
          <span>h</span>
          <span>e</span>
          <span>r</span>
          <span className="inline-block w-7"></span>
          <span>i</span>
          <span>d</span>
          <span>e</span>
          <span>a</span>
          <span>s</span>
          <span className="bubble-char-wrapper">
            <span>,</span>
            <span className="bubble-wrapper">
              <span className="hero-icon inline-flex items-center justify-center"><Bubble width={null} height={null} /></span>
            </span>
          </span>

          <span className="hero-line-break"></span>
          
          <span className="whitespace-nowrap">
            <span>d</span>
            <span>e</span>
            <span className="italic">s</span>
            <span>i</span>
            <span>g</span>
            <span>n</span>
            <span className="inline-block w-7"></span>
            <span>a</span>
            <span>n</span>
            <span>d</span>
            <span className="inline-block w-7"></span>
            <span>t</span>
            <span>e</span>
            <span>c</span>
            <span>h</span>
            <span>n</span>
            <span>o</span>
            <span>l</span>
            <span>o</span>
            <span>g</span>
            <span>y</span>
            <span className="inline-block w-7"></span>
            <span>t</span>
            <span className="italic">o</span>
          </span>

          <span className="hero-line-break"></span>
          
          <span className="whitespace-nowrap">
            <span>c</span>
            <span>r</span>
            <span>e</span>
            <span>a</span>
            <span>t</span>
            <span>e</span>
            <span className="inline-block w-7"></span>
            <span>e</span>
            <span>x</span>
            <span>t</span>
            <span>r</span>
            <span>a</span>
            <span>o</span>
            <span>r</span>
            <span>d</span>
            <span>i</span>
            <span>n</span>
            <span>a</span>
            <span>r</span>
            <span>y</span>
            <span className="inline-block w-7"></span>
            <span>c</span>
            <span>u</span>
            <span>s</span>
            <span>t</span>
            <span>o</span>
            <span className="italic">m</span>
            <span>e</span>
            <span>r</span>
          </span>

          <span className="hero-line-break"></span>
          
          <span className="whitespace-nowrap">
            <span>e</span>
            <span>x</span>
            <span>p</span>
            <span>e</span>
            <span>r</span>
            <span>i</span>
            <span className="italic">e</span>
            <span>n</span>
            <span>c</span>
            <span>e</span>
            <span>s</span>
            <span>.</span>

            <span style={{ display: 'inline-block', verticalAlign: 'middle', transform: 'translateY(-25%)' }}><span className="ml-8 hero-icon inline-flex items-center justify-center"><ArrowHandDrawn className="scale-150" /></span></span>
          </span>
        </h1>
      </div>

      {/* Mobile version */}
      <div className="animated-text-mobile animated-text-mobile--about lg:hidden w-full max-w-120rem global-padding mx-auto flex-1 flex items-center justify-center pb-[3.125rem]">
        <h1
          className="heading-1-lg mx-auto about-text-container"
          aria-label="We bring together ideas, design and technology to create extraordinary customer experiences."
        >
          <span>W</span>
          <span>e</span>
          <span className="inline-block w-3"></span>
          <span>b</span>
          <span>r</span>
          <span>i</span>
          <span>n</span>
          <span>g</span>
          <span className="bubble-char-wrapper">
            <span className="bubble-wrapper">
              <span className="hero-icon inline-flex items-center justify-center"><Bubble width={null} height={null} /></span>
            </span>
          </span>
          
          <span className="hero-line-break"></span>
          
          <span>t</span>
          <span className="italic">o</span>
          <span>g</span>
          <span>e</span>
          <span>t</span>
          <span>h</span>
          <span>e</span>
          <span>r</span>
          <span className="inline-block w-3"></span>
          <span>i</span>
          <span>d</span>
          <span>e</span>
          <span>a</span>
          <span>s</span>
          <span>,</span>

          <span className="hero-line-break"></span>
          {/* <span className="inline-block w-3"></span> */}

          <span>d</span>
          <span>e</span>
          <span className="italic">s</span>
          <span>i</span>
          <span>g</span>
          <span>n</span>
          <span className="inline-block w-3"></span>
          <span>a</span>
          <span>n</span>
          <span>d</span>

          <span className="hero-line-break"></span>
          
          <span>t</span>
          <span>e</span>
          <span>c</span>
          <span>h</span>
          <span>n</span>
          <span>o</span>
          <span>l</span>
          <span>o</span>
          <span>g</span>
          <span>y</span>
          <span className="inline-block w-3"></span>
          <span>t</span>
          <span className="italic">o</span>

          <span className="hero-line-break"></span>
          
          <span>c</span>
          <span>r</span>
          <span>e</span>
          <span>a</span>
          <span>t</span>
          <span>e</span>
          <span className="inline-block w-3"></span>
          <span style={{ display: 'inline-block', verticalAlign: 'middle', transform: 'translateY(-15%)' }}><span className="ml-4 hero-icon inline-flex items-center justify-center"><ArrowHandDrawn className="scale-150" /></span></span>

          <span className="hero-line-break"></span>

          <span>e</span>
          <span>x</span>
          <span>t</span>
          <span>r</span>
          <span>a</span>
          <span>o</span>
          <span>r</span>
          <span>d</span>
          <span>i</span>
          <span>n</span>
          <span>a</span>
          <span>r</span>
          <span>y</span>

          <span className="hero-line-break"></span>
          
          <span>c</span>
          <span>u</span>
          <span>s</span>
          <span>t</span>
          <span>o</span>
          <span className="italic">m</span>
          <span>e</span>
          <span>r</span>
          
          <span className="hero-line-break"></span>

          <span>e</span>
          <span>x</span>
          <span>p</span>
          <span>e</span>
          <span>r</span>
          <span>i</span>
          <span className="italic">e</span>
          <span>n</span>
          <span>c</span>
          <span>e</span>
          <span>s</span>
          <span>.</span>
        </h1>
      </div>

      <div 
        className={`scroll-for-more hidden lg:flex flex-col items-center gap-2 transition-opacity duration-300 cursor-pointer mt-10 mb-4 ${scrollOpacity === 0 ? 'pointer-events-none' : ''}`}
        style={{ opacity: scrollOpacity }}
        onClick={() => {
          window.scrollTo({
            top: window.innerHeight,
            behavior: 'smooth'
          });
        }}
      >
        <p className="text-center !text-[0.75rem] uppercase">SCROLL FOR MORE</p>
        <Image src="/images/underline.svg" alt="" width={160} height={12} className="mx-auto" priority />
      </div>
    </section>
  );
}
